import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Spin,
  Alert,
  Divider,
} from "antd";
import {
  UserOutlined,
  BookOutlined,
  FileTextOutlined,
  CrownOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import PieChart from "./PieChart";
// import DailyPeaksChart from './DailyPeaksChart';
const { Title } = Typography;

const API_URL = process.env.REACT_APP_API_URL;
const fetchConfig = {
  credentials: "include",
  headers: { Accept: "application/json" },
};

const AnalyticsView = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userEngagement, setUserEngagement] = useState(null);
  const [courseMetrics, setCourseMetrics] = useState(null);
  const [creatorSegments, setCreatorSegments] = useState(null);
  const [peakMetrics, setPeakMetrics] = useState(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);

        // Fetch data with better error handling
        const fetchWithErrorHandling = async (url) => {
          const response = await fetch(`${API_URL}${url}`, {
            ...fetchConfig,
            method: "GET",
          });
          if (!response.ok) {
            const text = await response.text();
            console.error(`Error response from ${url}:`, text);
            throw new Error(`API error (${response.status}): ${text}`);
          }
          try {
            return await response.json();
          } catch (e) {
            console.error(`JSON parse error for ${url}:`, e);
            throw new Error(`Failed to parse JSON from ${url}`);
          }
        };

        const [engagement, metrics, segments, peaks] = await Promise.all([
          fetchWithErrorHandling("/api/user_engagement/28"),
          fetchWithErrorHandling("/api/course_metrics/28"),
          fetchWithErrorHandling("/api/creator_segments/28"),
          fetchWithErrorHandling("/api/peak_metrics/28"),
        ]);

        console.log("Fetched data:", { engagement, metrics, segments, peaks });

        setUserEngagement(engagement);
        setCourseMetrics(metrics);
        setCreatorSegments(segments);
        setPeakMetrics(peaks);
      } catch (err) {
        console.error("Analytics fetch error:", err);
        setError(err.message || "Failed to fetch analytics data");
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        style={{ margin: "24px" }}
      />
    );
  }

  return (
    <div className="analytics-container">
      <Title level={2}>Analytics Dashboard</Title>
      <Title level={4}>
        User Engagement (Last 28 Days)
        <Divider />
        <Row gutter={[16, 16]} className="stats-row">
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total New Users"
                value={userEngagement?.total_new_users}
                prefix={<UserOutlined />}
                suffix="users"
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Engagement Rate"
                value={(
                  (userEngagement?.active_creators /
                    userEngagement?.total_new_users) *
                  100
                ).toFixed(1)}
                suffix="%"
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={12}>
            <Card title="Creator Distribution">
              <PieChart data={creatorSegments} />
            </Card>
          </Col>
        </Row>
      </Title>
      <Divider />
      {/* Overall Statistics */}
      <Title level={4}>Overall Statistics</Title>
      <Row gutter={[16, 16]} className="stats-row">
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Active Creators"
              value={courseMetrics?.active_creators}
              prefix={<TeamOutlined />}
              suffix="creators"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Courses"
              value={courseMetrics?.total_courses}
              prefix={<BookOutlined />}
              suffix="courses"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Premium Courses"
              value={courseMetrics?.total_premium_courses}
              prefix={<CrownOutlined />}
              suffix="courses"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Premium Creators"
              value={courseMetrics?.premium_creator_count}
              prefix={<CrownOutlined />}
              suffix="creators"
            />
          </Card>
        </Col>
      </Row>

      {/* Premium Creator Statistics */}
      <Title level={4} style={{ marginTop: "24px" }}>
        Premium Creator Statistics
      </Title>
      <Row gutter={[16, 16]} className="stats-row">
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Premium Courses Created"
              value={courseMetrics?.premium_creator_premium_courses}
              prefix={<CrownOutlined />}
              suffix="courses"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Free Courses Created"
              value={courseMetrics?.premium_creator_free_courses}
              prefix={<BookOutlined />}
              suffix="courses"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Premium Courses/Creator"
              value={courseMetrics?.avg_premium_courses_per_premium_creator}
              prefix={<CrownOutlined />}
              precision={1}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Free Courses/Creator"
              value={courseMetrics?.avg_free_courses_per_premium_creator}
              prefix={<BookOutlined />}
              precision={1}
            />
          </Card>
        </Col>
      </Row>

      {/* Premium Course Metrics */}
      <Title level={4} style={{ marginTop: "24px" }}>
        Premium Course Metrics
      </Title>
      <Row gutter={[16, 16]} className="stats-row">
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Avg Pages (Premium Creator)"
              value={
                courseMetrics?.avg_pages_per_premium_creator_premium_course
              }
              prefix={<FileTextOutlined />}
              precision={1}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Avg Chapters (Premium Creator)"
              value={
                courseMetrics?.avg_chapters_per_premium_creator_premium_course
              }
              prefix={<FileTextOutlined />}
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Avg Characters (Premium Creator)"
              value={
                courseMetrics?.avg_chars_per_premium_creator_premium_course
              }
              prefix={<FileTextOutlined />}
              precision={0}
            />
          </Card>
        </Col>
      </Row>

      {/* Non-Premium Creator Statistics */}
      <Title level={4} style={{ marginTop: "24px" }}>
        Non-Premium Creator Statistics
      </Title>
      <Row gutter={[16, 16]} className="stats-row">
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Premium Courses Created"
              value={courseMetrics?.non_premium_creator_premium_courses}
              prefix={<CrownOutlined />}
              suffix="courses"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Free Courses Created"
              value={courseMetrics?.non_premium_creator_free_courses}
              prefix={<BookOutlined />}
              suffix="courses"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Pages (Premium Courses)"
              value={
                courseMetrics?.avg_pages_per_non_premium_creator_premium_course
              }
              prefix={<FileTextOutlined />}
              precision={1}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Pages (Free Courses)"
              value={
                courseMetrics?.avg_pages_per_non_premium_creator_free_course
              }
              prefix={<FileTextOutlined />}
              precision={1}
            />
          </Card>
        </Col>
      </Row>

      {/* Content Length Metrics */}
      <Title level={4} style={{ marginTop: "24px" }}>
        Content Length Metrics
      </Title>
      <Row gutter={[16, 16]} className="stats-row">
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Chars (Premium Creator - Premium)"
              value={
                courseMetrics?.avg_chars_per_premium_creator_premium_course
              }
              prefix={<FileTextOutlined />}
              precision={0}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Chars (Premium Creator - Free)"
              value={courseMetrics?.avg_chars_per_premium_creator_free_course}
              prefix={<FileTextOutlined />}
              precision={0}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Chars (Non-Premium - Premium)"
              value={
                courseMetrics?.avg_chars_per_non_premium_creator_premium_course
              }
              prefix={<FileTextOutlined />}
              precision={0}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Chars (Non-Premium - Free)"
              value={
                courseMetrics?.avg_chars_per_non_premium_creator_free_course
              }
              prefix={<FileTextOutlined />}
              precision={0}
            />
          </Card>
        </Col>
      </Row>

      {/* Peak Usage Metrics */}
      <Title level={4} style={{ marginTop: "24px" }}>
        Peak Usage Metrics
      </Title>
      <Row gutter={[16, 16]} className="stats-row">
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Tasks per Peak Hour"
              value={peakMetrics?.peak_hour_summary?.avg_tasks_per_peak_hour}
              prefix={<BookOutlined />}
              precision={1}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Premium Tasks per Peak"
              value={
                peakMetrics?.peak_hour_summary?.avg_premium_tasks_per_peak_hour
              }
              prefix={<CrownOutlined />}
              precision={1}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Pages per Peak Hour"
              value={peakMetrics?.peak_hour_summary?.avg_pages_per_peak_hour}
              prefix={<FileTextOutlined />}
              precision={1}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Characters per Peak Hour"
              value={peakMetrics?.peak_hour_summary?.avg_chars_per_peak_hour}
              prefix={<FileTextOutlined />}
              precision={0}
            />
          </Card>
        </Col>
      </Row>

      {/* Load Distribution */}
      <Title level={5} style={{ marginTop: "16px" }}>
        Daily Load Distribution
      </Title>
      <Row gutter={[16, 16]} className="stats-row">
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="High Load Hours per Day"
              value={peakMetrics?.load_distribution?.high_load_hours_per_day}
              suffix="hours"
              precision={1}
            />
            <Statistic
              title="Avg Tasks during High Load"
              value={peakMetrics?.load_distribution?.high_load_avg_tasks}
              suffix="tasks/hour"
              precision={1}
              style={{ marginTop: "16px" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Medium Load Hours per Day"
              value={peakMetrics?.load_distribution?.medium_load_hours_per_day}
              suffix="hours"
              precision={1}
            />
            <Statistic
              title="Avg Tasks during Medium Load"
              value={peakMetrics?.load_distribution?.medium_load_avg_tasks}
              suffix="tasks/hour"
              precision={1}
              style={{ marginTop: "16px" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Low Load Hours per Day"
              value={peakMetrics?.load_distribution?.low_load_hours_per_day}
              suffix="hours"
              precision={1}
            />
            <Statistic
              title="Avg Tasks during Low Load"
              value={peakMetrics?.load_distribution?.low_load_avg_tasks}
              suffix="tasks/hour"
              precision={1}
              style={{ marginTop: "16px" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Peak Patterns */}
      <Title level={5} style={{ marginTop: "16px" }}>
        Peak Patterns
      </Title>
      <Row gutter={[16, 16]} className="stats-row">
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Average Peak Duration"
              value={peakMetrics?.peak_patterns?.avg_peak_duration_hours}
              suffix="hours"
              precision={1}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Common Peak Hours"
              value={`${peakMetrics?.peak_patterns?.common_peak_start_hour}:00 - ${peakMetrics?.peak_patterns?.common_peak_end_hour}:00`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={12}>
          <Card>
            <Statistic
              title="Secondary Peak Times"
              value={
                peakMetrics?.peak_patterns?.secondary_peaks?.join(", ") ||
                "None"
              }
              style={{ wordBreak: "break-word" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Busiest Hours */}
      <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title={`Busiest Hour (${peakMetrics?.busiest_hours?.hour_of_day}:00)`}
              value={peakMetrics?.busiest_hours?.avg_tasks}
              suffix="tasks/hour"
              precision={1}
            />
            <Statistic
              title="Premium Tasks"
              value={peakMetrics?.busiest_hours?.avg_premium_tasks}
              suffix="tasks/hour"
              precision={1}
              style={{ marginTop: "16px" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title={`Quietest Hour (${peakMetrics?.quietest_hours?.hour_of_day}:00)`}
              value={peakMetrics?.quietest_hours?.avg_tasks}
              suffix="tasks/hour"
              precision={1}
            />
            <Statistic
              title="Premium Tasks"
              value={peakMetrics?.quietest_hours?.avg_premium_tasks}
              suffix="tasks/hour"
              precision={1}
              style={{ marginTop: "16px" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Daily Peaks Chart */}
      {/* <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                <Col xs={24}>
                    <Card title="Daily Peak Tasks Distribution">
                        <DailyPeaksChart data={peakMetrics?.daily_peaks || []} />
                    </Card>
                </Col>
            </Row> */}
    </div>
  );
};

export default AnalyticsView;
